import { useEffect, useRef } from 'react';
import { softphone } from '../lib/softphone';

/**
 * The signal ring: the caller's voice radiates outward, the agent's voice
 * inward, around whoever is on the line. Idle or ringing, it breathes in the
 * agent's state colour. With reduced motion it is drawn once, still.
 */
export function SignalRing({
  color,
  mode,
  size = 320,
  children,
}: {
  color: string;
  mode: 'idle' | 'ringing' | 'live' | 'hold';
  size?: number;
  children?: React.ReactNode;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    el.width = size * dpr;
    el.height = size * dpr;
    const g = el.getContext('2d');
    if (!g) return;
    g.scale(dpr, dpr);
    const resolved = getComputedStyle(el).getPropertyValue('color') || color;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const bands = 72;
    const cx = size / 2;
    const cy = size / 2;
    const r = size * 0.34;
    let raf = 0;
    const smooth = { remote: new Array(bands).fill(0), local: new Array(bands).fill(0) };

    const draw = (t: number) => {
      g.clearRect(0, 0, size, size);
      const lv = mode === 'live' ? softphone.levels(bands) : null;
      const breath = mode === 'live' ? 0 : (Math.sin(t / (mode === 'ringing' ? 180 : 900)) + 1) / 2;

      // base ring
      g.beginPath();
      g.arc(cx, cy, r, 0, Math.PI * 2);
      g.strokeStyle = resolved;
      g.globalAlpha = mode === 'hold' ? 0.25 : 0.35 + breath * 0.25;
      g.lineWidth = 2;
      g.stroke();

      // soft halo
      const halo = g.createRadialGradient(cx, cy, r * 0.6, cx, cy, r * 1.45);
      halo.addColorStop(0, 'transparent');
      halo.addColorStop(0.55, resolved);
      halo.addColorStop(1, 'transparent');
      g.globalAlpha = mode === 'live' ? 0.06 : 0.03 + breath * 0.07;
      g.fillStyle = halo;
      g.fillRect(0, 0, size, size);

      for (let i = 0; i < bands; i++) {
        const a = (i / bands) * Math.PI * 2 - Math.PI / 2;
        const target = lv ? lv.remote[i] : 0;
        const targetIn = lv ? lv.local[i] : 0;
        smooth.remote[i] += (target - smooth.remote[i]) * 0.35;
        smooth.local[i] += (targetIn - smooth.local[i]) * 0.35;
        const idle = mode === 'live' ? 0.02 : 0.015 + breath * (mode === 'ringing' ? 0.09 : 0.03) * (0.6 + 0.4 * Math.sin(i * 0.7 + t / 400));
        const out = Math.max(idle, smooth.remote[i]) * size * 0.13;
        const inn = Math.max(idle * 0.6, smooth.local[i]) * size * 0.07;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        g.globalAlpha = mode === 'hold' ? 0.3 : 0.9;
        g.lineWidth = 2;
        g.lineCap = 'round';
        g.beginPath();
        g.moveTo(cx + cos * (r + 6), cy + sin * (r + 6));
        g.lineTo(cx + cos * (r + 6 + out), cy + sin * (r + 6 + out));
        g.stroke();
        g.globalAlpha = mode === 'hold' ? 0.2 : 0.5;
        g.beginPath();
        g.moveTo(cx + cos * (r - 6), cy + sin * (r - 6));
        g.lineTo(cx + cos * (r - 6 - inn), cy + sin * (r - 6 - inn));
        g.stroke();
      }
      g.globalAlpha = 1;
      if (!still) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [color, mode, size]);

  return (
    <div className="relative grid aspect-square place-items-center" style={{ width: `min(${size}px, 100%)` }}>
      <canvas ref={canvas} style={{ color }} className="absolute inset-0 h-full w-full" aria-hidden />
      <div className="relative z-10 flex flex-col items-center px-8 text-center">{children}</div>
    </div>
  );
}
